import { Component, Inject, Input, OnInit } from '@angular/core';
import { JobDetails } from '../../../../models/job-details.model';
import { Store } from '@ngrx/store';
import { JobState } from '../../../store/state/job-details.state';
import { IdentityService } from 'src/app/core/identity.service';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { WINDOW } from 'src/app/core/core.module';
import {
    MarginCalculatorAuthService,
    UserCredentials
} from 'src/app/margin-calculator/services/margin-calculator-auth.service';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, map } from 'rxjs';
import { HospitalType } from 'src/app/shared/lib/facility-rate/models/hospital-types.enum';
import { LinkSampleMarginToJobComponent } from '../../../dialog/nova-link-sample-margin-to-job.component';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';

@UntilDestroy()
@Component({
    selector: 'nova-create-margin-calculator',
    templateUrl: './create-margin-calculator.component.html',
    styleUrls: ['./create-margin-calculator.component.scss']
})

//Note: this component currently used in nova-locums-recruiter-submittal-info.
// the duplicated code exists in nova-recruiting-job-details-job-section.
// refactoring to remove the duplicated code will be done with https://ayadev.atlassian.net/browse/LTI-768
export class CreateMarginCalculatorComponent implements OnInit {
    @Input() job: JobDetails;
    showCreateMarginCalculator: boolean;
    currentUserFullName: string;
    showSampleMargin: boolean;
    isUserAllowedToCreateMargin: boolean;

    constructor(
        private readonly _store: Store<JobState>,
        private readonly _identityService: IdentityService,
        @Inject(APP_CONFIG) private readonly _settings: Settings,
        private readonly _marginAuthService: MarginCalculatorAuthService,
        @Inject(WINDOW) private readonly _window: Window,
        private readonly _dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.showCreateMarginCalculator = this._settings.showNewMarginCalculator;
        const currentUser = this._identityService.current;
        this.currentUserFullName = `${currentUser.firstName} ${currentUser.lastName}`;

        this.showSampleMargin = currentUser.permissionsToken.indexOf('Recruiting:Jobs-SampleMargin$c') !== -1;

        this._marginAuthService
            .getUserCredentials()
            .pipe(
                map((userCredentials: UserCredentials) =>
                    userCredentials === null ? false : userCredentials.isRecruiter || userCredentials.isNurseContract
                ),
                untilDestroyed(this)
            )
            .subscribe((e) => (this.isUserAllowedToCreateMargin = e));
    }

    newMarginCalcTooltip(): string {
        if (this.disableNewMarginCalc()) {
            if (!this.job.sampleMarginId) {
                return 'A sample margin is required for Workforce Disruption jobs. Please reach out to a recruitment manager to have one created';
            } else {
                return 'Please use the Sample Margin for Workforce Disruption jobs.';
            }
        }
        return '';
    }

    disableNewMarginCalc(): boolean {
        const isWfd =
            this.job.hospitalType === HospitalType.WorkforceDisruptionClinical ||
            this.job.hospitalType === HospitalType.WorkforceDisruptionNonClinical;
        return isWfd && !this.showSampleMargin;
    }

    createNewMargin(jobId: number): void {
        this._window.open(
            `${this._window.location.origin}/#/margin-calculator/${this._identityService.current.contactId}/job/${jobId}`,
            '_blank',
            'width=1366,height=768,resizable=1,scrollbars=1,titlebar=0,location=0,status=0'
        );
    }

    linkMargin(jobId: number): void {
        this._dialog
            .open(LinkSampleMarginToJobComponent, {
                panelClass: 'nova__dialog',
                data: {
                    jobId: jobId
                }
            })
            .afterClosed()
            .pipe(
                filter((result) => result),
                untilDestroyed(this)
            )
            .subscribe((data) => {
                this.toast(`Sample margin ${data.sampleMarginId} linked to job ${jobId}`);
                this.job = {
                    ...this.job,
                    sampleMarginId: data.sampleMarginId
                };
            });
    }

    sampleMargin(id: number, sampleMarginId: number): void {
        this._window.open(
            `${this._window.location.origin}/#/margin-calculator/${id}/margin/${sampleMarginId}`,
            '_blank',
            'width=1366,height=768,resizable=1,scrollbars=1,titlebar=0,location=0,status=0'
        );
    }

    toast(message: string) {
        this._store.dispatch(showToasterMessage({ message: message, duration: 5000 }));
    }
}
